import { createLazyResource, createMutation } from '@magicdoorinc/ui';
import { createMemo, createSignal } from 'solid-js';
import { usePropertyManagerPermissions } from '~/contexts/permissions/PropertyManagerPermissionsContext';
import { createMagicDoorContext } from '~/contexts/utils';
import { PortfolioRepository } from '~/repositories/portfolioRepository';
import type { HydratedPortfolioDto, PortfolioOverviewDto } from '~/swagger/portal/Api';
const repo = new PortfolioRepository();

const LAST_USED_PORTFOLIO_KEY = 'last-used-portfolio';

export const [PortfoliosProvider, usePortfolios] = createMagicDoorContext('Portfolios', () => {
  const { propertyAccess, addPortfolioAccess } = usePropertyManagerPermissions();
  const [fullPortfolios, { refetch, mutate }] = createLazyResource(() => repo.getPortfolios({ active: true }));
  const [currentId, setCurrentId] = createSignal<string>(localStorage.getItem(LAST_USED_PORTFOLIO_KEY) ?? '');

  const portfolios = createMemo(() => {
    const access = propertyAccess();
    const allPortfolios = fullPortfolios();

    if (!access || !allPortfolios) return allPortfolios;

    if (access.allProperties) return allPortfolios;

    const allowedPortfolioIds = new Set(access.portfolioIds || []);
    const directPropertyIds = new Set(access.propertyIds || []);

    return allPortfolios.filter((portfolio) => {
      if (allowedPortfolioIds.has(portfolio.id)) return true;

      const hasDirectlyAssignedProperty = portfolio.properties?.some((property) => directPropertyIds.has(property.id));
      return hasDirectlyAssignedProperty;
    });
  });

  const current = createMemo(() => (currentId() ? portfolios()?.find((p) => p.id === currentId()) : undefined));
  const [portfolioOverview, setPortfolioOverview] = createSignal<PortfolioOverviewDto>();

  const addPortfolio = createMutation(async (name: string) => {
    const result = await repo.createPortfolio({ name });
    const hydrated: HydratedPortfolioDto = { ...result, properties: [], activePropertyCount: 0 };
    mutate((prev) => [...(prev ?? []), hydrated].sort((a, b) => a.name.localeCompare(b.name)));

    await addPortfolioAccess(result.id);

    return result;
  });

  /** Not preferred. use deactivatePortfolio instead in most cases */
  const deletePortfolio = createMutation(async (id: string) => {
    await repo.deletePortfolio(id);
    mutate((prev) => prev?.filter((p) => p.id !== id));
  });

  const deactivatePortfolio = createMutation(async (id: string, forceDeactivateChildren?: boolean) => {
    await repo.deactivatePortfolio(id, forceDeactivateChildren);
    mutate((prev) => prev?.filter((p) => p.id !== id));
    // TODO: maybe in the future we can use the deactivated portfolio in context
    // mutate((prev) => prev?.map((p) => (p.id === id ? { ...p, active: false } : p)));
  });

  const updatePortfolio = createMutation(async (id: string, name: string) => {
    const result = await repo.updatePortfolio(id, { name });
    mutate((prev) => prev?.map((p) => (p.id === id ? { ...p, ...result } : p)));
    return result;
  });

  const getPortfolioOverview = createMutation(async (portfolioId: string) => {
    const data = await repo.getPortfolioOverview(portfolioId, true);
    setPortfolioOverview(data);
  });

  return {
    portfolios,
    fullPortfolios,
    refetch,
    current,
    setCurrentId: (id?: string) => {
      id && localStorage.setItem(LAST_USED_PORTFOLIO_KEY, id);
      setCurrentId(id ?? '');
    },
    addPortfolio,
    deletePortfolio,
    deactivatePortfolio,
    updatePortfolio,
    getPortfolioOverview,
    portfolioOverview,
    currentId,
  };
});
